import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, RefreshCw, ExternalLink, ChevronDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatItalianDateTime } from "../lib/dateUtils";
import { Card } from "./ui-kit";

// Semaforo QR (backend qr_health.py): i QR delle produzioni si aprono e
// mostrano il lotto giusto? Stesso riquadro per cliente e consulente.
const TONE = {
  verde: { strip: "bg-emerald-500", icon: "bg-emerald-600", title: "text-emerald-800", label: "QR funzionanti" },
  giallo: { strip: "bg-amber-500", icon: "bg-amber-500", title: "text-amber-800", label: "QR funzionanti, dati da completare" },
  rosso: { strip: "bg-red-600", icon: "bg-red-600", title: "text-red-800", label: "QR con anomalie" },
  grigio: { strip: "bg-slate-300", icon: "bg-slate-400", title: "text-slate-600", label: "Nessun QR da verificare" },
};

const CLIENT_TEXT = {
  verde: "I QR delle produzioni degli ultimi 7 giorni si aprono e mostrano il lotto giusto.",
  giallo: "I QR si aprono, ma in alcune produzioni mancano dati da completare. Non blocca nulla.",
  rosso: "Alcuni QR non funzionano: controlla l'elenco qui sotto. Il consulente è già stato avvisato.",
  grigio: "Nessuna produzione negli ultimi 7 giorni: niente da verificare.",
};

function ProblemList({ problems, testid }) {
  return (
    <ul className="divide-y divide-hairline" data-testid={testid}>
      {problems.map((p, i) => (
        <li key={`${p.production_id || "server"}-${p.code}-${i}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-navy">
              <span className={`h-2 w-2 shrink-0 rounded-full ${p.severity === "rosso" ? "bg-red-500" : "bg-amber-500"}`} aria-hidden="true" />
              <span className="break-words">{p.lot_code ? `${p.recipe_name || ""} · lotto ${p.lot_code}` : "Server"}</span>
            </div>
            <p className="mt-0.5 text-sm text-slate-600 break-words">{p.message}</p>
          </div>
          {p.qr_url && (
            <a href={p.qr_url} target="_blank" rel="noreferrer"
              className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg border border-navy px-3 text-sm font-medium text-navy transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-gold"
              data-testid="qr-health-open-qr">
              <ExternalLink className="h-4 w-4" strokeWidth={1.5} /> Apri QR
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function Header({ status, text, checkedAt, onlineCheck, onRefresh, refreshing, canRefresh, children }) {
  const tone = TONE[status] || TONE.grigio;
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white ${tone.icon}`}>
          <QrCode className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Semaforo QR</p>
          <h2 className={`font-heading text-xl font-semibold leading-snug sm:text-2xl ${tone.title}`} data-testid="qr-health-status" data-status={status}>{tone.label}</h2>
          <p className="mt-1 text-sm text-slate-600 break-words">{text}</p>
          {children}
          <p className="mt-1 text-xs text-slate-400">
            Ultima verifica: {formatItalianDateTime(checkedAt, "non ancora eseguita")}
            {onlineCheck === false && " · la prova da internet si attiva solo sull'app online"}
          </p>
        </div>
      </div>
      {canRefresh && (
        <button type="button" onClick={onRefresh} disabled={refreshing}
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-lg border border-navy px-4 text-sm font-medium text-navy transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-gold disabled:opacity-60"
          data-testid="qr-health-refresh">
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} /> {refreshing ? "Verifica in corso…" : "Verifica ora"}
        </button>
      )}
    </div>
  );
}

function useQrHealth(path) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    api.get(path).then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [path]);
  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post(`${path}/refresh`);
      setData(d);
      toast.success(d.status === "rosso" ? "Verifica completata: ci sono QR con anomalie" : "Verifica dei QR completata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita"); }
    finally { setRefreshing(false); }
  };
  return { data, refresh, refreshing };
}

function Shell({ status, children, testid }) {
  const tone = TONE[status] || TONE.grigio;
  return (
    <Card className="relative overflow-hidden p-5 sm:p-6" data-testid={testid}>
      <div className={`absolute inset-y-0 left-0 w-1.5 ${tone.strip}`} aria-hidden="true" />
      {children}
    </Card>
  );
}

export function ClientQrHealthPanel({ canRefresh = true }) {
  const { data, refresh, refreshing } = useQrHealth("/app/qr-health");
  const [open, setOpen] = useState(false);
  if (data === null) return null;
  if (data === false) return null;
  const problems = data.problems || [];
  return (
    <Shell status={data.status} testid="qr-health-client">
      <Header status={data.status} text={CLIENT_TEXT[data.status] || CLIENT_TEXT.grigio} checkedAt={data.checked_at}
        onRefresh={refresh} refreshing={refreshing} canRefresh={canRefresh} />
      {problems.length > 0 && (
        <div className="mt-4 border-t border-hairline pt-3">
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="qr-health-toggle">
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
            {problems.length === 1 ? "1 segnalazione" : `${problems.length} segnalazioni`}
          </button>
          {open && <ProblemList problems={problems} testid="qr-health-problems" />}
        </div>
      )}
    </Shell>
  );
}

export function AdminQrHealthPanel() {
  const { data, refresh, refreshing } = useQrHealth("/admin/qr-health");
  const [openId, setOpenId] = useState(null);
  if (data === null) return null;
  if (data === false) {
    return <Shell status="grigio" testid="qr-health-admin"><p className="flex items-center gap-2 text-sm text-slate-500"><AlertTriangle className="h-4 w-4" strokeWidth={1.5} />Semaforo QR non disponibile in questo momento.</p></Shell>;
  }
  const { counts = {}, tenants = [] } = data;
  const attention = tenants.filter((t) => t.status === "rosso" || t.status === "giallo");
  const text = attention.length
    ? `${attention.length === 1 ? "1 azienda richiede" : `${attention.length} aziende richiedono`} attenzione. Ricevi una notifica e un'email per ogni nuova anomalia.`
    : "Tutti i QR delle aziende funzionano. Ricevi una notifica e un'email appena compare un'anomalia.";
  const chips = [["rosso", "con anomalie"], ["giallo", "da completare"], ["verde", "funzionanti"], ["grigio", "senza produzioni"]];
  return (
    <Shell status={data.status} testid="qr-health-admin">
      <Header status={data.status} text={text} checkedAt={data.checked_at} onlineCheck={data.online_check}
        onRefresh={refresh} refreshing={refreshing} canRefresh>
        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map(([s, label]) => (
            <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-hairline" data-testid={`qr-health-count-${s}`}>
              <span className={`h-2 w-2 rounded-full ${TONE[s].strip}`} aria-hidden="true" />{counts[s] || 0} {label}
            </span>
          ))}
        </div>
      </Header>
      {attention.length > 0 && (
        <ul className="mt-4 divide-y divide-hairline border-t border-hairline" data-testid="qr-health-tenants">
          {attention.map((t) => (
            <li key={t.tenant_id} className="py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button type="button" onClick={() => setOpenId(openId === t.tenant_id ? null : t.tenant_id)} aria-expanded={openId === t.tenant_id}
                  className="inline-flex min-h-[44px] min-w-0 items-center gap-2 rounded-lg text-left text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-gold"
                  data-testid="qr-health-tenant-toggle">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${TONE[t.status].strip}`} aria-hidden="true" />
                  <span className="break-words">{t.tenant_name}</span>
                  <span className="font-normal text-slate-500">· {t.problems.length === 1 ? "1 segnalazione" : `${t.problems.length} segnalazioni`}</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${openId === t.tenant_id ? "rotate-180" : ""}`} strokeWidth={1.5} />
                </button>
                <Link to={`/admin/tenants/${t.tenant_id}`} className="inline-flex min-h-[44px] items-center px-2 text-sm font-medium text-gold hover:underline focus:outline-none focus:ring-2 focus:ring-gold" data-testid="qr-health-tenant-open">Apri azienda</Link>
              </div>
              {openId === t.tenant_id && <div className="pl-5"><ProblemList problems={t.problems} testid="qr-health-tenant-problems" /></div>}
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}
